<template>
  <div class="bundled-form-container">
    <div class="generic-form" v-if="!packageData?.IsSoldOut && !packageData?.IsComingSoon">
      <form @submit.prevent="submit" novalidate>
        <small class="p-error" v-if="form.PackageId.$error">{{ form.PackageId.$errors[0].$message }}</small>
        <div class="asymmetric-input-group d-flex flex-v-end extra-items no-lines">
          <div class="input-holder">
            <label for="NumberOfPeople">Number of People*</label>
            <input type="number" id="NumberOfPeople" name="NumberOfPeople" v-model="request.NumberOfPeople" min="1" max="500" />
            <small class="p-error" v-if="form.NumberOfPeople.$error">{{ form.NumberOfPeople.$errors[0].$message }}</small>
          </div>
          <div class="input-holder">
            <label for="NumberOfRooms">How many rooms?*</label>
            <input type="number" id="NumberOfRooms" name="NumberOfRooms" v-model="request.NumberOfRooms" min="0" :max="request.NumberOfPeople" />
            <small class="p-error rooms-error" v-if="form.NumberOfRooms.$error">{{ form.NumberOfRooms.$errors[0].$message }}</small>
          </div>

          <div class="form-subtotal sub-option">
            <h3 v-if="BigNumber(subtotal).gt(0)">
              Subtotal: {{ formatCurrency(subtotal) }} <span><small class="text-mid-grey" v-if="packageData.DomesticInternational == 'Domestic'">+
                  GST</small></span>
            </h3>
          </div>
        </div>
        <NotEnoughStockWarning v-if="form.PackageId.stock.$invalid && request.NumberOfPeople > 0" />
        <div class="form-button">
          <button type="submit" class="btn" :loading="isSubmitting || isSubmitted">Book now</button>
        </div>
      </form>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { StartBundledBookingRequestModel, type RedirectUrlModel } from '@/components/BookingForm/types';
import type { ApiResult } from '@/types/ApiResult';
import type { BookingBundledPackageMetadataModel } from '@/types/BookingGameMetadataModel';
import useVuelidate from '@vuelidate/core';
import { helpers, maxValue, minValue, required } from '@vuelidate/validators';
import BigNumber from 'bignumber.js';
import { defaults } from 'lodash';
import { computed, ref } from 'vue';
import wretch from 'wretch';
import { formatCurrency } from './formatters';
import NotEnoughStockWarning from './NotEnoughStockWarning.vue';
import { inventorySoldOutValidator, inventoryStockValidator } from './validators';

const props = defineProps({
  package: String,
});

const isSubmitting = ref<boolean>(false);
const isSubmitted = ref<boolean>(false);
const packageData = ref<BookingBundledPackageMetadataModel>(props.package ? JSON.parse(props.package) : {});

const minNumberOfRooms = computed(() => {
  // For international packages, min number of rooms should be equal to the number of people divided by 2, rounded up #STH290
  if (request.value.NumberOfPeople != 0) {
    return packageData.value.DomesticInternational == 'International' ? Math.ceil(request.value.NumberOfPeople / 2) : 1
  }
  return 0;
});

const request = ref<StartBundledBookingRequestModel>(defaults(new StartBundledBookingRequestModel({ PackageId: packageData.value.PackageId })));
const rules = computed<Partial<Record<keyof StartBundledBookingRequestModel, unknown>>>(() => ({
  PackageId: {
    stock: helpers.withMessage(
      'Not enough stock',
      inventoryStockValidator(packageData.value?.MasterInventory, () => request.value.NumberOfPeople)
    ),
    soldOut: helpers.withMessage('Sold out!', inventorySoldOutValidator(packageData.value?.MasterInventory)),
  },
  NumberOfPeople: {
    required,
    min: helpers.withMessage('At least one', minValue(1)),
    max: helpers.withMessage('No more than 500', maxValue(500)),
    $autoDirty: true,
  },
  NumberOfRooms: {
    required: helpers.withMessage('Required', required),
    min: helpers.withMessage(() => `At least ${minNumberOfRooms.value}`, minValue(minNumberOfRooms)),
    max: helpers.withMessage(() => `No more than ${request.value.NumberOfPeople}`, maxValue(request.value.NumberOfPeople)),
    $autoDirty: true,
  },
}));
const form = useVuelidate<StartBundledBookingRequestModel>(rules, request);

async function submit() {
  if (!(await form.value.$validate())) {
    return;
  }
  try {
    isSubmitting.value = true;
    const resp = await wretch('/api/booking/start-booking').post(request.value).json<ApiResult<RedirectUrlModel>>();
    if (resp.successful && resp.data) {
      window.location.href = resp.data.RedirectUrl;
      isSubmitted.value = true;
    }
  } finally {
    isSubmitting.value = false;
  }
}

const subtotal = computed(() => {
  const subtotal = new BigNumber(packageData.value.MasterInventory.UnitRrpExclGst).times(request.value.NumberOfPeople || 0);
  return subtotal.toString();
});
</script>
